import { axiosDefaultInstance } from '@/http/default'

import type { BulkUpdateRequest, DeliveryNote, DeliveryNoteImages, DeliveryNoteRequest } from '@/model/DeliveryNote'
import type Page from '@/model/Page'
import type { ChartType, Statistics } from '@/model/Statistics'
import { useProfileStore } from '@/store/profile.module'
import type { AxiosInstance } from 'axios'
import { applyV2DeliveriesForApiRequest } from '@/utils/deliveryNoteMapping'
import { resolveTransportDocumentV2SchemaEnabled } from '@/utils/transportDocumentV2SchemaFlagUtils'
import handleError from './util/ErrorHandler'
import type { ServiceError } from './util/ServiceError'

type DeliveryNoteTextFilters = {
  originName?: string
  destinationName?: string
  customerName?: string
  customerVatNumber?: string
  driverName?: string
  driverSurname?: string
}

function appendTextFilterParams(params: string[][], filters: DeliveryNoteTextFilters): void {
  if (filters.originName) {
    params.push(['originName', filters.originName])
  }
  if (filters.destinationName) {
    params.push(['destinationName', filters.destinationName])
  }
  if (filters.customerName) {
    params.push(['customerName', filters.customerName])
  }
  if (filters.customerVatNumber) {
    params.push(['customerVatNumber', filters.customerVatNumber])
  }
  if (filters.driverName) {
    params.push(['driverName', filters.driverName])
  }
  if (filters.driverSurname) {
    params.push(['driverSurname', filters.driverSurname])
  }
}

class DeliveryNoteService {
  private axiosInstance: AxiosInstance
  private storeProfile: any

  constructor() {
    this.axiosInstance = axiosDefaultInstance
    // this.storeProfile = useProfileStore()
  }

  async findAll(
    startDate?: string,
    endDate?: string,
    customerIds?: string[],
    employeeIds?: string[],
    driverIds?: string[],
    driverNifs?: string[],
    vehicleIds?: string[],
    originIds?: string[],
    destinationIds?: string[],
    originName?: string,
    destinationName?: string,
    customerName?: string,
    customerVatNumber?: string,
    driverName?: string,
    driverSurname?: string,
    billed?: boolean,
    uncompleted?: boolean,
    manualValidationRequired?: boolean,
    sort?: string,
    direction?: string,
    payload?: string,
    payloadIds?: string[]
  ): Promise<DeliveryNote[] | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []

    if (startDate) {
      params.push(['startDate', startDate])
    }

    if (endDate) {
      params.push(['endDate', endDate])
    }

    if (customerIds !== undefined) {
      customerIds.forEach((id) => params.push(['customerId', id]))
    }

    if (employeeIds !== undefined) {
      employeeIds.forEach((id) => params.push(['employeeId', id]))
    }

    if (driverIds !== undefined) {
      driverIds.forEach((id) => params.push(['driverId', id]))
    }

    if (driverNifs !== undefined) {
      driverNifs.forEach((nif) => params.push(['driverNif', nif]))
    }

    if (vehicleIds !== undefined) {
      vehicleIds.forEach((id) => params.push(['vehicleId', id]))
    }

    if (originIds !== undefined) {
      originIds.forEach((id) => params.push(['originId', id]))
    }

    if (destinationIds !== undefined) {
      destinationIds.forEach((id) => params.push(['destinationId', id]))
    }

    appendTextFilterParams(params, {
      originName,
      destinationName,
      customerName,
      customerVatNumber,
      driverName,
      driverSurname
    })

    if (payload) {
      params.push(['payload', payload])
    }

    if (payloadIds !== undefined) {
      payloadIds.forEach((id) => params.push(['payloadId', id]))
    }

    if (billed !== undefined) {
      params.push(['billed', String(billed)])
    }

    if (uncompleted !== undefined) {
      params.push(['uncompleted', String(uncompleted)])
    }

    if (manualValidationRequired !== undefined) {
      params.push(['manualValidationRequired', String(manualValidationRequired)])
    }

    if (sort) {
      params.push(['sort', sort])
    }

    if (direction) {
      params.push(['direction', direction])
    }

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<DeliveryNote[]>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findAllV2(
    pageNumber?: any,
    pageSize?: any,
    startDate?: string,
    endDate?: string,
    customerIds?: string[],
    employeeIds?: string[],
    driverIds?: string[],
    driverNifs?: string[],
    vehicleIds?: string[],
    originIds?: string[],
    destinationIds?: string[],
    originName?: string,
    destinationName?: string,
    customerName?: string,
    customerVatNumber?: string,
    driverName?: string,
    driverSurname?: string,
    billed?: boolean,
    uncompleted?: boolean,
    manualValidationRequired?: boolean,
    sort?: string,
    direction?: string,
    numbers?: string[],
    payload?: string,
    payloadIds?: string[],
  ): Promise<Page<DeliveryNote> | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []

    if (numbers !== undefined && numbers.length > 0) {
      numbers.forEach((number) => params.push(['numbers', number]))
    }

    if (pageSize) {
      params.push(['pageSize', pageSize])
    }
    if (pageNumber) {
      params.push(['pageNumber', pageNumber])
    }

    if (startDate) {
      params.push(['startDate', startDate])
    }

    if (endDate) {
      params.push(['endDate', endDate])
    }

    if (customerIds !== undefined) {
      customerIds.forEach((id) => params.push(['customerId', id]))
    }

    if (employeeIds !== undefined) {
      employeeIds.forEach((id) => params.push(['employeeId', id]))
    }

    if (driverIds !== undefined) {
      driverIds.forEach((id) => params.push(['driverId', id]))
    }

    if (driverNifs !== undefined) {
      driverNifs.forEach((nif) => params.push(['driverNif', nif]))
    }

    if (vehicleIds !== undefined) {
      vehicleIds.forEach((id) => params.push(['vehicleId', id]))
    }

    if (originIds !== undefined) {
      originIds.forEach((id) => params.push(['originId', id]))
    }

    if (destinationIds !== undefined) {
      destinationIds.forEach((id) => params.push(['destinationId', id]))
    }

    appendTextFilterParams(params, {
      originName,
      destinationName,
      customerName,
      customerVatNumber,
      driverName,
      driverSurname
    })

    if (payload) {
      params.push(['payload', payload])
    }

    if (payloadIds !== undefined) {
      payloadIds.forEach((id) => params.push(['payloadId', id]))
    }

    if (billed !== undefined) {
      params.push(['billed', String(billed)])
    }

    if (uncompleted !== undefined) {
      params.push(['uncompleted', String(uncompleted)])
    }

    if (manualValidationRequired !== undefined) {
      params.push(['manualValidationRequired', String(manualValidationRequired)])
    }

    if (sort) {
      params.push(['sort', sort])
    }

    if (direction) {
      params.push(['direction', direction])
    }

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Page<DeliveryNote>>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/v2`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findById(id: string): Promise<DeliveryNote | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<DeliveryNote>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${id}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async getDeliveryNoteImages(id: string): Promise<DeliveryNoteImages | ServiceError> {
    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<DeliveryNoteImages>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${id}/images`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async addImageToDeliveryNote(id: string, images: Blob[]): Promise<DeliveryNote | ServiceError> {
    const companyId: string = this.storeProfile.selectedCompany.id

    const formData: FormData = new FormData()
    images.forEach((image) => formData.append('image', image))

    try {
      const networkResponse = await this.axiosInstance.post<DeliveryNote>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${id}/images`,
        formData,
        {
          headers: {
            'Content-Type': undefined
          }
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async deleteDeliveryNoteImage(id: string, imageId: string): Promise<DeliveryNote | ServiceError> {
    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.delete<DeliveryNote>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${id}/image/${imageId}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadPdfById(id: string): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${id}/pdf`,
        {
          params: { locale },
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadCmrPdfById(id: string): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${id}/cmr/pdf`,
        {
          params: { locale },
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async createDeliveryNote(
    deliveryNote: DeliveryNoteRequest
  ): Promise<DeliveryNote | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id
    const v2Enabled = resolveTransportDocumentV2SchemaEnabled(
      this.storeProfile.getCompanyFeatureFlags(companyId),
      this.storeProfile.globalFeatureFlags
    )
    const payload = applyV2DeliveriesForApiRequest(deliveryNote, undefined, v2Enabled)

    try {
      const networkResponse = await this.axiosInstance.post<DeliveryNote>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note`,
        payload
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateDeliveryNote(
    deliveryNote: DeliveryNoteRequest,
    existingNote?: DeliveryNote
  ): Promise<DeliveryNote | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id
    const v2Enabled = resolveTransportDocumentV2SchemaEnabled(
      this.storeProfile.getCompanyFeatureFlags(companyId),
      this.storeProfile.globalFeatureFlags
    )
    const payload = applyV2DeliveriesForApiRequest(deliveryNote, existingNote, v2Enabled)

    try {
      const networkResponse = await this.axiosInstance.put<DeliveryNote>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${deliveryNote.id}`,
        payload
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async deleteDeliveryNote(id: string): Promise<DeliveryNote | ServiceError> {
    this.storeProfile = useProfileStore()

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.delete<DeliveryNote>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/${id}`
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async findStatistics(
    statisticFilters: ChartType[],
    startDate: string,
    endDate: string,
    employeeIds: string[],
    vehicleIds: string[],
    driverIds?: string[],
    driverNifs?: string[],
    billed?: boolean,
    uncompleted?: boolean,

    manualValidationRequired?: boolean
  ): Promise<Statistics | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []

    if (employeeIds !== undefined) {
      employeeIds.forEach((id) => params.push(['employeeId', id]))
    }

    if (vehicleIds !== undefined) {
      vehicleIds.forEach((id) => params.push(['vehicleId', id]))
    }

    if (driverIds !== undefined) {
      driverIds.forEach((id) => params.push(['driverId', id]))
    }

    if (driverNifs !== undefined) {
      driverNifs.forEach((nif) => params.push(['driverNif', nif]))
    }

    if (billed !== undefined) {
      params.push(['billed', String(billed)])
    }

    if (uncompleted !== undefined) {
      params.push(['uncompleted', String(uncompleted)])
    }

    if (manualValidationRequired !== undefined) {
      params.push(['manualValidationRequired', String(manualValidationRequired)])
    }

    if (statisticFilters) {
      statisticFilters.forEach((f) => params.push(['statisticsFilters', String(f)]))
    }

    params.push(['startDate', startDate])
    params.push(['endDate', endDate])

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<any>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/statistics`,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadAllCsv(
    startDate?: string,
    endDate?: string,
    numbers?: string[],
    customerIds?: string[],
    employeeIds?: string[],
    driverIds?: string[],
    driverNifs?: string[],
    vehicleIds?: string[],
    originIds?: string[],
    destinationIds?: string[],
    originName?: string,
    destinationName?: string,
    customerName?: string,
    customerVatNumber?: string,
    driverName?: string,
    driverSurname?: string,
    billed?: boolean,
    uncompleted?: boolean,
    manualValidationRequired?: boolean,
    sort?: string,
    direction?: string,
    payload?: string,
    payloadIds?: string[],
    columns?: string[]
  ): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const params: string[][] = []

    if (startDate) {
      params.push(['startDate', startDate])
    }

    if (endDate) {
      params.push(['endDate', endDate])
    }

    if (numbers !== undefined) {
      numbers.forEach((number) => params.push(['numbers', number]))
    }

    if (customerIds !== undefined) {
      customerIds.forEach((id) => params.push(['customerId', id]))
    }

    if (employeeIds !== undefined) {
      employeeIds.forEach((id) => params.push(['employeeId', id]))
    }

    if (driverIds !== undefined) {
      driverIds.forEach((id) => params.push(['driverId', id]))
    }

    if (driverNifs !== undefined) {
      driverNifs.forEach((nif) => params.push(['driverNif', nif]))
    }

    if (vehicleIds !== undefined) {
      vehicleIds.forEach((id) => params.push(['vehicleId', id]))
    }

    if (originIds !== undefined) {
      originIds.forEach((id) => params.push(['originId', id]))
    }

    if (destinationIds !== undefined) {
      destinationIds.forEach((id) => params.push(['destinationId', id]))
    }

    appendTextFilterParams(params, {
      originName,
      destinationName,
      customerName,
      customerVatNumber,
      driverName,
      driverSurname
    })

    if (payload) {
      params.push(['payload', payload])
    }

    if (payloadIds !== undefined) {
      payloadIds.forEach((id) => params.push(['payloadId', id]))
    }

    if (billed !== undefined) {
      params.push(['billed', String(billed)])
    }

    if (uncompleted !== undefined) {
      params.push(['uncompleted', String(uncompleted)])
    }

    if (manualValidationRequired !== undefined) {
      params.push(['manualValidationRequired', String(manualValidationRequired)])
    }

    if (sort) {
      params.push(['sort', sort])
    }

    if (direction) {
      params.push(['direction', direction])
    }
    params.push(['locale', locale])

    if (columns && columns.length > 0) {
      columns.forEach((column) => params.push(['columns', column]))
    }

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/csv`,
        {
          params: searchParams,
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadAllPdf(
    startDate?: string,
    endDate?: string,
    customerIds?: string[],
    employeeIds?: string[],
    driverIds?: string[],
    driverNifs?: string[],
    vehicleIds?: string[],
    originIds?: string[],
    destinationIds?: string[],
    originName?: string,
    destinationName?: string,
    customerName?: string,
    customerVatNumber?: string,
    driverName?: string,
    driverSurname?: string,
    billed?: boolean,
    uncompleted?: boolean,
    manualValidationRequired?: boolean,
    numbers?: string[],
    payload?: string,
    payloadIds?: string[]
  ): Promise<Blob | ServiceError> {
    this.storeProfile = useProfileStore()
    const locale = localStorage.getItem('appLanguage') || 'es'

    const params: string[][] = []

    if (startDate) {
      params.push(['startDate', startDate])
    }

    if (endDate) {
      params.push(['endDate', endDate])
    }

    if (numbers !== undefined && numbers.length > 0) {
      numbers.forEach((number) => params.push(['numbers', number]))
    }

    if (customerIds !== undefined) {
      customerIds.forEach((id) => params.push(['customerId', id]))
    }

    if (employeeIds !== undefined) {
      employeeIds.forEach((id) => params.push(['employeeId', id]))
    }

    if (driverIds !== undefined) {
      driverIds.forEach((id) => params.push(['driverId', id]))
    }

    if (driverNifs !== undefined) {
      driverNifs.forEach((nif) => params.push(['driverNif', nif]))
    }

    if (vehicleIds !== undefined) {
      vehicleIds.forEach((id) => params.push(['vehicleId', id]))
    }

    if (originIds !== undefined) {
      originIds.forEach((id) => params.push(['originId', id]))
    }

    if (destinationIds !== undefined) {
      destinationIds.forEach((id) => params.push(['destinationId', id]))
    }

    appendTextFilterParams(params, {
      originName,
      destinationName,
      customerName,
      customerVatNumber,
      driverName,
      driverSurname
    })

    if (payload) {
      params.push(['payload', payload])
    }

    if (payloadIds !== undefined) {
      payloadIds.forEach((id) => params.push(['payloadId', id]))
    }

    if (billed !== undefined) {
      params.push(['billed', String(billed)])
    }

    if (uncompleted !== undefined) {
      params.push(['uncompleted', String(uncompleted)])
    }

    if (manualValidationRequired !== undefined) {
      params.push(['manualValidationRequired', String(manualValidationRequired)])
    }

    params.push(['locale', locale])

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.get<Blob>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/pdf`,
        {
          params: searchParams,
          responseType: 'blob'
        }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }

  async updateBulk(
    bulkUpdateRequest: BulkUpdateRequest,
    startDate?: string,
    endDate?: string,
    numbers?: string[],
    customerIds?: string[],
    employeeIds?: string[],
    driverIds?: string[],
    driverNifs?: string[],
    vehicleIds?: string[],
    originIds?: string[],
    destinationIds?: string[],
    originName?: string,
    destinationName?: string,
    customerName?: string,
    customerVatNumber?: string,
    driverName?: string,
    driverSurname?: string,
    billed?: boolean,
    uncompleted?: boolean,
    manualValidationRequired?: boolean,
    payload?: string,
    payloadIds?: string[]
  ): Promise<DeliveryNote[] | ServiceError> {
    this.storeProfile = useProfileStore()

    const params: string[][] = []

    if (startDate) {
      params.push(['startDate', startDate])
    }

    if (endDate) {
      params.push(['endDate', endDate])
    }

    if (numbers !== undefined && numbers.length > 0) {
      numbers.forEach((number) => params.push(['numbers', number]))
    }

    if (customerIds !== undefined) {
      customerIds.forEach((id) => params.push(['customerId', id]))
    }

    if (employeeIds !== undefined) {
      employeeIds.forEach((id) => params.push(['employeeId', id]))
    }

    if (driverIds !== undefined) {
      driverIds.forEach((id) => params.push(['driverId', id]))
    }

    if (driverNifs !== undefined) {
      driverNifs.forEach((nif) => params.push(['driverNif', nif]))
    }

    if (vehicleIds !== undefined) {
      vehicleIds.forEach((id) => params.push(['vehicleId', id]))
    }

    if (originIds !== undefined) {
      originIds.forEach((id) => params.push(['originId', id]))
    }

    if (destinationIds !== undefined) {
      destinationIds.forEach((id) => params.push(['destinationId', id]))
    }

    appendTextFilterParams(params, {
      originName,
      destinationName,
      customerName,
      customerVatNumber,
      driverName,
      driverSurname
    })

    if (payload) {
      params.push(['payload', payload])
    }

    if (payloadIds !== undefined) {
      payloadIds.forEach((id) => params.push(['payloadId', id]))
    }

    if (billed !== undefined) {
      params.push(['billed', String(billed)])
    }

    if (uncompleted !== undefined) {
      params.push(['uncompleted', String(uncompleted)])
    }

    if (manualValidationRequired !== undefined) {
      params.push(['manualValidationRequired', String(manualValidationRequired)])
    }

    const searchParams = new URLSearchParams(params)

    const companyId: string = this.storeProfile.selectedCompany.id

    try {
      const networkResponse = await this.axiosInstance.put<DeliveryNote[]>(
        `${import.meta.env.VITE_API_BASE_URL}/company/${companyId}/delivery-note/bulk`,
        bulkUpdateRequest,
        { params: searchParams }
      )
      return networkResponse.data
    } catch (error) {
      return handleError(error)
    }
  }
}

export const deliveryNoteService = new DeliveryNoteService()
